/**
 * K17 T1.12 — AI Quota Widget
 *
 * Kompakt visning av inneværende måneds KI-forbruk. Rolle-aware:
 *   - member-rolle: viser egen organisasjons kvote
 *   - admin: aggregat over eget studieforbund
 *   - systemadmin: aggregat over alle
 *
 * Kobler seg mot GET /api/admin/ai-quotas. Fanger 401/403 stille.
 */
import { useEffect, useState } from 'react';
import { Zap, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface MemberQuota {
  member_id: string;
  organization_name: string;
  tokens_included: number | null;
  tokens_used: number | null;
  cost_nok: number | null;
  tokens_used_subsidized: number | null;
  cost_nok_subsidized: number | null;
  overage_policy: string | null;
  warned_80pct_at: string | null;
  warned_100pct_at: string | null;
  blocked_at: string | null;
  last_aggregated_at: string | null;
}

interface QuotaResponse {
  current_period: { year: number; month: number };
  members: MemberQuota[];
}

function fmt(n: number | null): string {
  if (n == null) return '0';
  return Number(n).toLocaleString('no-NO');
}

function AggregateBar({ used, included }: { used: number; included: number }) {
  const pct = included > 0 ? Math.min(200, Math.round((used / included) * 100)) : 0;
  const barColor =
    pct >= 100 ? 'bg-red-500' :
    pct >= 80 ? 'bg-kf-orange-500' :
    pct >= 50 ? 'bg-yellow-400' :
    'bg-emerald-500';
  return (
    <div className="w-full">
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="text-xs text-gray-600 mt-1">{pct}% brukt</div>
    </div>
  );
}

export default function AiQuotaWidget() {
  const [data, setData] = useState<QuotaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch('/api/admin/ai-quotas');
        if (res.status === 401 || res.status === 403) {
          if (!cancelled) { setLoading(false); setError('no_access'); }
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) { setData(json); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e instanceof Error ? e.message : 'unknown'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="h-5 w-32 bg-gray-200 rounded mb-3" />
        <div className="h-2 w-full bg-gray-200 rounded" />
      </div>
    );
  }

  if (error === 'no_access' || !data) return null;
  if (error) return null;  // Widget skal ikke støye ved feil

  const totalUsed = data.members.reduce((s, m) => s + (m.tokens_used || 0), 0);
  const totalIncluded = data.members.reduce((s, m) => s + (m.tokens_included || 0), 0);
  const totalCostNok = data.members.reduce((s, m) => s + Number(m.cost_nok || 0), 0);
  const totalSubsidized = data.members.reduce((s, m) => s + (m.tokens_used_subsidized || 0), 0);
  const anyOverage = data.members.some(m => (m.tokens_used || 0) > (m.tokens_included || 0));
  const anyWarn80 = data.members.some(m => m.warned_80pct_at && !m.warned_100pct_at);
  const anyBlocked = data.members.some(m => m.blocked_at);

  const monthLabel = `${String(data.current_period.month).padStart(2, '0')}/${data.current_period.year}`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-kf-blue-50 rounded">
            <Zap className="h-4 w-4 text-kf-blue-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">KI-forbruk</div>
            <div className="text-xs text-gray-500">Måned {monthLabel}</div>
          </div>
        </div>
        {anyBlocked && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
            <AlertCircle className="h-3 w-3" /> Blokkert
          </span>
        )}
        {!anyBlocked && anyOverage && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
            <AlertCircle className="h-3 w-3" /> Over kvote
          </span>
        )}
        {!anyBlocked && !anyOverage && anyWarn80 && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-kf-orange-700 bg-kf-orange-50 px-2 py-0.5 rounded-full">
            <TrendingUp className="h-3 w-3" /> Over 80%
          </span>
        )}
        {!anyBlocked && !anyOverage && !anyWarn80 && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            <CheckCircle className="h-3 w-3" /> Innenfor kvote
          </span>
        )}
      </div>

      {data.members.length === 1 ? (
        <>
          <div className="text-sm text-gray-900 mb-1">
            <span className="font-medium">{fmt(totalUsed)}</span>
            <span className="text-gray-500"> / {fmt(totalIncluded)} tokens</span>
          </div>
          <AggregateBar used={totalUsed} included={totalIncluded || 1000000} />
          <div className="mt-2 text-xs text-gray-600">
            Beregnet overføring så langt: <span className="font-medium">{totalCostNok.toFixed(2)} NOK</span>
          </div>
          {totalSubsidized > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
              <span>KI-validering (dekket av Kompetanseforbundet)</span>
              <span className="font-medium">{fmt(totalSubsidized)} tokens · 0 NOK</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="text-sm text-gray-900 mb-2">
            {data.members.length} organisasjoner · {fmt(totalUsed)} av {fmt(totalIncluded)} tokens
          </div>
          <AggregateBar used={totalUsed} included={totalIncluded || 1000000} />
          <div className="mt-2 text-xs text-gray-600">
            Estimert overføring aggregert: <span className="font-medium">{totalCostNok.toFixed(2)} NOK</span>
          </div>
          {totalSubsidized > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
              <span>KI-validering (gratis, dekket av Kompetanseforbundet)</span>
              <span className="font-medium">{fmt(totalSubsidized)} tokens · 0 NOK</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
